import { useQuery, useMutation, useQueryClient, QueryKey } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { crudFactory } from '../genericCrudFactory';
import { AuthClient } from '../authClient';
import { LoginDto, MeDto, RefreshTokenDto, RegisterDto } from 'helpers/jwt-token-access/authTypes';

export const useRegisterMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: RegisterDto) => AuthClient.register(data),
    onSuccess: () => {
      toast.success('Registration successful');
      queryClient.invalidateQueries({ queryKey: ['auth'] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message ?? 'Registration failed.');
    },
  });
};

export const useLoginMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: LoginDto) => AuthClient.login(data),
    onSuccess: () => {
      toast.success('Login successful');
      queryClient.invalidateQueries({ queryKey: ['auth'] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message ?? 'Login failed.');
    },
  });
};

export const useRefreshMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: RefreshTokenDto) => AuthClient.refresh(data),
    onSuccess: () => {
      toast.success('Token refreshed successfully');
      queryClient.invalidateQueries({ queryKey: ['auth'] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message ?? 'Token refresh failed.');
    },
  });
};

export const useLogoutMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => AuthClient.logout(),
    onSuccess: () => {
      toast.success('Logged out successfully');
      queryClient.invalidateQueries({ queryKey: ['auth'] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message ?? 'Logout failed.');
    },
  });
};

export const useLogoutSessionMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) => AuthClient.logoutSession(sessionId),
    onSuccess: () => {
      toast.success('Session logged out successfully');
      queryClient.invalidateQueries({ queryKey: ['auth'] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message ?? 'Session logout failed.');
    },
  });
};

export const useCurrentUserQuery = () => {
  return useQuery<MeDto>({
    queryKey: ['auth', 'me'],
    queryFn: () => AuthClient.getCurrentUser(),
    staleTime: 5 * 60 * 1000, // optional: 5 minutes
    retry: false,
  });
};
